// A espinha do documento: `tripServices` → `tripStops` → `groupStopsByDay`, mais
// `tripAgenda` e `tripCost`. Lifted verbatim de
// `apps/clients/web-chat/src/ui/trips/derive.ts` — sem `t()`, sem locale: nada
// aqui traduz nada, então nenhuma função ganha `ctx`.
import {
  flightLegDirection,
  isCancelledFlightLeg,
  liveFlightSegments,
  travelerFacingLocator,
  type BaggageAllowance,
  type FlightLiveStatus,
  type TripView,
  type VoucherStatus,
} from '@voll-intelligence/domain';
import { localHHMM, localWallClock, parseLocalIso, startOfDay } from './dates.js';
import { flightDurationLabel } from './flight_duration.js';
import { formatFlightDuration } from './format.js';
import { rebookingIndex, type RebookedFrom } from './rebooked_flight.js';
import { partyLeg, sameJourneys, sharedValue } from './same_journey.js';
import { MODAL_ORDER, canonicalModal, type ModalKind } from './status.js';

/** "HH:MM" de um Date local (wall-clock), zero-padded. */
function hhmm(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Check-in do hotel como Date local-naive: dia (UTC) do `checkIn` + hora do
 *  `checkInHour` ("HH:MM"), alinhando com o wall-clock das linhas de voo. */
function hotelDayAt(day: Date | null, hour: string | null): Date | null {
  if (!day) return null;
  const m = hour ? /(\d{1,2}):(\d{2})/.exec(hour) : null;
  return new Date(
    day.getUTCFullYear(),
    day.getUTCMonth(),
    day.getUTCDate(),
    Number(m?.[1] ?? 0),
    Number(m?.[2] ?? 0),
  );
}

export interface AgendaRow {
  key: string;
  modal: ModalKind;
  at: Date | null; // wall-clock local p/ ordenar + coluna de data
  time: string; // "HH:MM" ou ""
  status: VoucherStatus | null;
  /** O viajante pediu o cancelamento deste voucher (ainda CONFIRMED). Null sem pedido. */
  cancellationRequestedAt: Date | null;
  // voo
  leg?: 'ida' | 'volta';
  flightNo?: string | null;
  route?: string | null; // "GRU → JFK" (voo/ônibus)
  // hotel
  hotelName?: string | null;
  nights?: number | null;
  checkIn?: Date | null;
  checkOut?: Date | null;
  // genérico
  supplier?: string | null;
}

export type Voucher = TripView['vouchers'][number];

/** Uma linha por TRECHO (não por voucher): a agenda dia-a-dia mostra ida e
 *  volta como entradas separadas, ao contrário de `tripServices`. */
function flightAgendaRows(voucher: Voucher): AgendaRow[] {
  if (voucher.details?.kind !== 'FLIGHT') return [];
  return voucher.details.segments.map((segment, index) => ({
    key: `${voucher.id}-${String(index)}`,
    modal: 'FLIGHT',
    at: parseLocalIso(segment.departLocal) ?? segment.departAt,
    time: localHHMM(segment.departLocal, segment.departAt),
    // A ida cancelada sozinha na companhia (#6394) é uma linha morta; o voucher segue vivo.
    status: isCancelledFlightLeg(segment) ? 'CANCELLED' : voucher.status,
    cancellationRequestedAt: voucher.cancellationRequestedAt ?? null,
    leg: flightLegDirection(segment) === 'RETURN' ? 'volta' : 'ida',
    flightNo: segment.flightNumbers.join(' ') || null,
    route:
      segment.departureAirport && segment.arrivalAirport
        ? `${segment.departureAirport} → ${segment.arrivalAirport}`
        : null,
  }));
}

/** Uma linha ancorada no CHECK-IN (não no check-out) — é o dia em que a
 *  estadia aparece na agenda; check-out sai como campo, não como entrada própria. */
function hotelAgendaRow(voucher: Voucher): AgendaRow | null {
  if (voucher.details?.kind !== 'HOTEL') return null;
  const details = voucher.details;
  const at = hotelDayAt(details.checkIn, details.checkInHour);
  return {
    key: voucher.id,
    modal: 'HOTEL',
    at,
    time: details.checkInHour && at ? hhmm(at) : '',
    status: voucher.status,
    cancellationRequestedAt: voucher.cancellationRequestedAt ?? null,
    hotelName: details.name,
    nights: details.nights,
    checkIn: details.checkIn,
    checkOut: details.checkOut,
  };
}

/** O fallback dos modais sem trecho próprio (BUS online, RENTAL, MOBILITY):
 *  uma linha ancorada no início do `journey`, sem a expansão que voo/hotel têm. */
function genericAgendaRow(voucher: Voucher, modal: ModalKind): AgendaRow {
  const journey = voucher.journey;
  const at = localWallClock(journey?.startLocal, journey?.startAt ?? null);
  return {
    key: voucher.id,
    modal,
    at,
    time: localHHMM(journey?.startLocal, journey?.startAt ?? null),
    status: voucher.status,
    cancellationRequestedAt: voucher.cancellationRequestedAt ?? null,
    route:
      journey?.origin && journey.destination ? `${journey.origin} → ${journey.destination}` : null,
    supplier: voucher.supplier,
  };
}

/** Despacha pelo formato do voucher: voo expande em várias linhas, hotel pode
 *  não ter linha nenhuma (guard de nulidade), o resto cai no genérico. */
function agendaRowsForVoucher(voucher: Voucher): AgendaRow[] {
  const modal = canonicalModal(voucher.modal);
  if (!modal) return [];
  if (voucher.details?.kind === 'FLIGHT') return flightAgendaRows(voucher);
  const hotel = hotelAgendaRow(voucher);
  if (hotel) return [hotel];
  return [genericAgendaRow(voucher, modal)];
}

/** Serviço que HOSPEDA (o hotel) em vez de MOVER. A distinção existe por uma razão só, e
 *  ela é de dado: a hora de check-in é quando o quarto FICA PRONTO, não quando a pessoa
 *  chega — o hotel do dia da ida costuma abrir às 14h enquanto o voo dela sai às 22h. Um
 *  horário de partida é prazo que se perde; um check-in espera por você. */
function isStay(modal: ModalKind): boolean {
  return modal === 'HOTEL';
}

/**
 * A régua do itinerário, a mesma em toda vista da viagem (lista, painel, texto de
 * compartilhamento, PDF e o destaque do topo): o dia de calendário do início; dentro do
 * MESMO dia, quem move antes de quem hospeda (`isStay`) — o hotel fecha o dia; por fim a
 * hora. Sem data vai ao fim.
 *
 * Não usa `max(check-in, chegada do voo)` para posicionar o hotel: erra o voo que pousa
 * depois da meia-noite (o hotel voltaria para antes dele) e segue pondo a corrida da noite
 * antes do hotel. O preço aceito da régua simples é esse mesmo: o que move no dia do
 * check-in, mesmo depois da hora da recepção, vem antes do hotel.
 */
function byItinerary(
  a: { at: Date | null; modal: ModalKind },
  b: { at: Date | null; modal: ModalKind },
): number {
  if (!a.at || !b.at) return Number(!a.at) - Number(!b.at);
  return (
    startOfDay(a.at).getTime() - startOfDay(b.at).getTime() ||
    Number(isStay(a.modal)) - Number(isStay(b.modal)) ||
    a.at.getTime() - b.at.getTime()
  );
}

/** Agenda dia-a-dia da viagem a partir dos vouchers. Voos expandem por trecho
 *  (ida/volta em linhas próprias); demais modais = uma linha. Ordena pela régua do
 *  itinerário (`byItinerary`); linhas sem data vão ao fim. */
export function tripAgenda(trip: TripView): AgendaRow[] {
  return trip.vouchers.flatMap(agendaRowsForVoucher).sort(byItinerary);
}

// ── Linha do tempo de SERVIÇOS (card lateral da sidebar) ─────────────────────
// Granularidade DIFERENTE da `tripAgenda`: aqui uma entrada = UM voucher (o
// serviço contratado), com os trechos aninhados dentro dela — voo ida+volta é
// uma entrada só. A agenda da home é uma linha por trecho/dia, enxuta de
// propósito. As duas projeções leem os mesmos `trip.vouchers` e compartilham os
// helpers deste arquivo; nenhuma substitui a outra.

/** Um trecho de deslocamento (voo ou ônibus) dentro de um serviço. Os campos
 *  exclusivos de um modal são opcionais (voo: duração/paradas/bagagem/nº;
 *  ônibus: endereços das estações). */
/** Um voo de um trecho com conexão, como a tela o lê. Mesmos três estados do `live` do trecho. */
export interface ServiceHop {
  from: string | null;
  to: string | null;
  departInstant: Date | null;
  arriveInstant: Date | null;
  live?: FlightLiveStatus | null;
}

export interface ServiceLeg {
  key: string;
  /** null no ônibus comprado online (a fonte não marca a perna). */
  leg: 'ida' | 'volta' | null;
  from: string | null;
  to: string | null;
  fromAddress?: string | null;
  toAddress?: string | null;
  /** O relógio de PAREDE do aeroporto lido no fuso do processo (`parseLocalIso(departLocal)`),
   *  o que o documento ordena, agrupa por dia e escreve em `departTime`. NÃO é o instante:
   *  numa volta LIS→GRU vista de São Paulo fica 4h à frente dele. */
  departAt: Date | null;
  arriveAt: Date | null;
  /** O INSTANTE verdadeiro (UTC) que a fonte deu — o único relógio comparável com `now`, com o
   *  `observedAt` do vivo e com as estimativas da companhia; uma janela medida em `departAt`
   *  abre e fecha na hora errada fora do fuso do aparelho. Null quando a fonte não datou. */
  departInstant: Date | null;
  arriveInstant: Date | null;
  departTime: string;
  arriveTime: string;
  /** Chegada no dia seguinte à partida (sufixo "+1" no horário). */
  arriveNextDay: boolean;
  duration?: string | null;
  stops?: number | null;
  carrier: string | null;
  number?: string | null;
  travelClass: string | null;
  seat: string | null;
  baggage?: string | null;
  /** Bagagem despachada incluída (o rótulo é do componente, não do modelo). */
  checkedBaggage?: boolean | null;
  /** A franquia que a TARIFA declara (peças/peso, despacho e mão) — o único campo que diz
   *  quanto. Ausente no trecho de ônibus e nos vouchers espelhados antes dela existir. */
  baggageAllowance?: BaggageAllowance | null;
  locator: string | null;
  /** O voo como está OPERANDO — portão, terminal, atraso — vindo do monitoramento
   *  e não do voucher. Ausente = ninguém anexou estado vivo nesta leitura;
   *  `null` = monitorado e nada observado ainda. Quem desenha tem de saber a
   *  diferença, e a regra de FRESCOR é de quem desenha: um portão visto há seis
   *  horas manda a pessoa para o lado errado do aeroporto. */
  live?: FlightLiveStatus | null;
  /** Os voos de um trecho com CONEXÃO (#4244), cada um com o seu relógio e o seu vivo: o `live`
   *  acima fala do voo que a pessoa embarca primeiro, e na conexão ela espera o PRÓXIMO. Ausente
   *  no trecho direto — o trecho é o voo. */
  hops?: ServiceHop[];
}

/** Detalhe do hotel e das agências: as formas do contrato de domínio, reusadas
 *  verbatim (`VoucherDetails`) em vez de redeclaradas aqui. */
type HotelDetail = Extract<NonNullable<Voucher['details']>, { kind: 'HOTEL' }>;
export type ServiceAgency = NonNullable<
  Extract<NonNullable<Voucher['details']>, { kind: 'RENTAL' }>['pickupAgency']
>;

export type TripServiceDetail =
  | { kind: 'FLIGHT'; legs: ServiceLeg[] }
  | { kind: 'BUS'; legs: ServiceLeg[] }
  | HotelDetail
  | {
      kind: 'RENTAL';
      carModel: string | null;
      carGroup: string | null;
      /** #6004 — the rental company's code, the key of the kit's logo bank. */
      companyCode: string | null;
      /** #5032 — absent on a doc mirrored before these fields. */
      transmission: 'AUTOMATIC' | 'MANUAL' | null;
      airConditioning: boolean | null;
      pickup: ServiceAgency | null;
      dropoff: ServiceAgency | null;
      pickupAt: Date | null;
      dropoffAt: Date | null;
      pickupTime: string;
      dropoffTime: string;
    }
  | {
      kind: 'MOBILITY';
      category: string | null;
      distance: number | null;
      duration: string | null;
      route: string | null;
    };

/** Um serviço da viagem = um voucher. `at`/`time` são o início no wall-clock
 *  local (ordenação + calha da linha do tempo); `detail` é nulo quando a fonte
 *  ainda não enriqueceu o voucher (só fica completa em CONFIRMED). */
export interface TripService {
  key: string;
  /** O voucher CRU desta entrada. A projeção achata o que a linha do tempo
   *  precisa; o documento do painel lê daqui o que ela deixa de fora — os `hops`
   *  de cada trecho, o viajante, o solicitante e o selo de política. Buscar o
   *  voucher de volta pela `key` não serve: ela cai num sintético
   *  (`modal-posição`) quando a fonte não deu `id`, e aí não casa com nada. */
  voucher: Voucher;
  modal: ModalKind;
  status: VoucherStatus | null;
  voucherCode: string | null;
  reservationNumber: string | null;
  supplier: string | null;
  amount: number | null;
  currency: string | null;
  at: Date | null;
  time: string;
  /** Fim do serviço no mesmo referencial de `at`: a chegada da última perna, o
   *  check-out do hotel, a devolução do carro, o fim da corrida. Null quando a fonte
   *  não datou o fim — e aí ninguém pode afirmar que o serviço AINDA está em curso. */
  endsAt: Date | null;
  detail: TripServiceDetail | null;
  /** Voo remarcado: o voo que este substituiu (`TripView.rebookings`). Ausente fora do par. */
  rebookedFrom?: RebookedFrom;
}

/** Chegada num dia de calendário POSTERIOR à partida — o "+1" do horário.
 *  Compara dias de calendário (via `startOfDay`), não o número do dia do mês:
 *  `getDate()` sozinho lê 31→1 como "mesmo dia" (dias diferentes de meses
 *  diferentes com o mesmo número) e um trecho de 26h (2 dias) como "+1" só. */
function nextDay(departAt: Date | null, arriveAt: Date | null): boolean {
  if (departAt == null || arriveAt == null) return false;
  return startOfDay(arriveAt).getTime() > startOfDay(departAt).getTime();
}

/** Os números de voo de UM trecho, "1769/7748". A fonte manda mais de um quando
 *  o trecho tem escala, e o separador tinha de deixar de ser espaço: colado ao
 *  lado da companhia ("GOL 1769 7748") o par lia como um número só e comprido. */
function joinFlightNumbers(numbers: string[]): string | null {
  return numbers.join('/') || null;
}

type FlightSeg = Extract<NonNullable<Voucher['details']>, { kind: 'FLIGHT' }>['segments'][number];

/** Um `ServiceLeg` por segmento de bilhete — a fonte já separa ida/volta em
 *  segmentos distintos, então a perna é 1:1 com o segmento (ao contrário do
 *  ônibus, que às vezes não marca lado nenhum). */
function flightLegs(segments: FlightSeg[]): ServiceLeg[] {
  // O trecho que o atendimento cancelou sozinho na companhia (#6394) não é passo, próximo
  // voo nem card vivo. Sai DEPOIS de mapear, para a chave `f-<i>` seguir a do segmento. Com
  // todos cancelados o voucher já está morto e mostra o que era: nada sai.
  const live = liveFlightSegments(segments);
  const shown = live.length > 0 ? new Set(live) : new Set(segments);
  return segments.flatMap((s, i) => (shown.has(s) ? [flightLeg(s, i)] : []));
}

function flightLeg(s: FlightSeg, i: number): ServiceLeg {
  const departAt = parseLocalIso(s.departLocal) ?? s.departAt;
  const arriveAt = parseLocalIso(s.arriveLocal) ?? s.arriveAt;
  return {
    key: `f-${String(i)}`,
    leg: flightLegDirection(s) === 'RETURN' ? 'volta' : 'ida',
    from: s.departureAirport,
    to: s.arrivalAirport,
    departAt,
    arriveAt,
    departInstant: s.departAt,
    arriveInstant: s.arriveAt,
    departTime: localHHMM(s.departLocal, s.departAt),
    arriveTime: localHHMM(s.arriveLocal, s.arriveAt),
    arriveNextDay: nextDay(departAt, arriveAt),
    duration: flightDurationLabel(s),
    stops: s.stops,
    carrier: s.airline,
    number: joinFlightNumbers(s.flightNumbers),
    travelClass: s.cabinClass,
    seat: s.seat,
    baggage: s.baggage,
    checkedBaggage: s.checkedBaggage,
    baggageAllowance: s.baggageAllowance ?? null,
    live: s.live,
    ...(s.hops && s.hops.length > 1 ? { hops: s.hops.map(serviceHop) } : {}),
    // O código que o viajante leva ao balcão: status do fornecedor não é código (#6368).
    locator: travelerFacingLocator(s),
  };
}

function serviceHop(hop: NonNullable<FlightSeg['hops']>[number]): ServiceHop {
  return {
    from: hop.departureAirport,
    to: hop.arrivalAirport,
    departInstant: hop.departAt,
    arriveInstant: hop.arriveAt,
    ...(hop.live === undefined ? {} : { live: hop.live }),
  };
}

type BusSeg = Extract<NonNullable<Voucher['details']>, { kind: 'BUS' }>['segments'][number];

/** `leg` fica `null` no ônibus comprado online: a fonte só marca `BACK` para a
 *  volta, então qualquer coisa que não seja `OUTWARD`/`BACK` não pode virar "ida". */
function busLegs(segments: BusSeg[]): ServiceLeg[] {
  return segments.map((s, i) => {
    const idaOrNull = s.type === 'OUTWARD' ? 'ida' : null;
    return {
      key: `b-${String(i)}`,
      leg: s.type === 'BACK' ? 'volta' : idaOrNull,
      from: s.departureStation,
      to: s.arrivalStation,
      fromAddress: s.departureAddress,
      toAddress: s.arrivalAddress,
      departAt: localWallClock(null, s.departAt),
      arriveAt: localWallClock(null, s.arriveAt),
      // Sem relógio de parede na fonte, o instante É o que o ônibus tem.
      departInstant: s.departAt,
      arriveInstant: s.arriveAt,
      departTime: localHHMM(null, s.departAt),
      arriveTime: localHHMM(null, s.arriveAt),
      arriveNextDay: nextDay(s.departAt, s.arriveAt),
      carrier: s.company,
      travelClass: s.seatClass,
      seat: s.seat,
      locator: s.locator,
    };
  });
}

/** Janela do primeiro trecho DATADO (voo/ônibus). */
function legsWindow(legs: ServiceLeg[]): { at: Date | null; time: string } {
  const dated = legs.flatMap((leg) =>
    leg.departAt ? [{ at: leg.departAt, time: leg.departTime }] : [],
  );
  dated.sort((a, b) => a.at.getTime() - b.at.getTime());
  const first = dated[0];
  return { at: first?.at ?? null, time: first?.time ?? '' };
}

/** Um instante do wall-clock local + seu "HH:MM": a string local-naive quando a
 *  fonte a tem, senão o relógio UTC do instante de fallback — o dia e a hora da
 *  mesma leitura (`localWallClock` com `localHHMM`). */
function localWindow(
  local: string | null | undefined,
  at: Date | null | undefined,
): { at: Date | null; time: string } {
  return { at: localWallClock(local, at ?? null), time: localHHMM(local, at ?? null) };
}

/** Janela da jornada do voucher (locação, corrida, voucher sem detalhe). */
function journeyWindow(voucher: Voucher): { at: Date | null; time: string } {
  return localWindow(voucher.journey?.startLocal, voucher.journey?.startAt);
}

/** Início do serviço no wall-clock local + "HH:MM": primeiro trecho datado /
 *  check-in do hotel / início da jornada. `at` null quando a fonte não datou. */
function serviceWindow(
  voucher: Voucher,
  detail: TripServiceDetail | null,
): { at: Date | null; time: string } {
  if (detail?.kind === 'FLIGHT' || detail?.kind === 'BUS') return legsWindow(detail.legs);
  if (detail?.kind !== 'HOTEL') return journeyWindow(voucher);
  const at = hotelDayAt(detail.checkIn, detail.checkInHour);
  return { at, time: at && detail.checkInHour ? hhmm(at) : '' };
}

/** FIM do serviço, no mesmo referencial local do início — a hora em que ele deixa de ser
 *  o que a viagem pede. Espelha o `serviceWindow` porque a pergunta é a mesma vista do
 *  outro lado: chegada da ÚLTIMA perna (o trecho de volta fecha o voucher), check-out do
 *  hotel, devolução do carro, fim da corrida. Null quando a fonte não datou o fim. */
function serviceEnd(voucher: Voucher, detail: TripServiceDetail | null): Date | null {
  if (detail?.kind === 'FLIGHT' || detail?.kind === 'BUS') {
    const arrivals = detail.legs.flatMap((leg) => (leg.arriveAt ? [leg.arriveAt.getTime()] : []));
    return arrivals.length > 0 ? new Date(Math.max(...arrivals)) : null;
  }
  if (detail?.kind === 'HOTEL') return hotelDayAt(detail.checkOut, detail.checkOutHour);
  return localWindow(voucher.journey?.endLocal, voucher.journey?.endAt).at;
}

type MobilityDetails = Extract<NonNullable<Voucher['details']>, { kind: 'MOBILITY' }>;
type RentalDetails = Extract<NonNullable<Voucher['details']>, { kind: 'RENTAL' }>;

/** Rota da corrida vem do `journey`, não do `details` — a fonte de mobilidade
 *  não carrega origem/destino no bloco de detalhe do modal. */
function mobilityDetail(voucher: Voucher, details: MobilityDetails): TripServiceDetail {
  const j = voucher.journey;
  return {
    kind: 'MOBILITY',
    category: details.category,
    distance: details.distance,
    duration: formatFlightDuration(details.duration),
    route: j?.origin && j.destination ? `${j.origin} → ${j.destination}` : null,
  };
}

/** Retirada/devolução vêm do `journey` (a fonte não data o bloco `details` de
 *  locação); `pickupAgency`/`dropoffAgency` são os únicos campos que já chegam prontos. */
function rentalDetail(voucher: Voucher, details: RentalDetails): TripServiceDetail {
  const j = voucher.journey;
  const pickup = localWindow(j?.startLocal, j?.startAt);
  const dropoff = localWindow(j?.endLocal, j?.endAt);
  return {
    kind: 'RENTAL',
    carModel: details.carModel,
    carGroup: details.carGroup,
    companyCode: details.companyCode ?? null,
    transmission: details.transmission ?? null,
    airConditioning: details.airConditioning ?? null,
    pickup: details.pickupAgency,
    dropoff: details.dropoffAgency,
    pickupAt: pickup.at,
    dropoffAt: dropoff.at,
    pickupTime: pickup.time,
    dropoffTime: dropoff.time,
  };
}

/** Bloco de detalhe do modal a partir de `voucher.details` (a jornada completa a
 *  janela de locação/corrida). Null quando a fonte ainda não enriqueceu o
 *  voucher: a entrada da linha do tempo sobrevive com código, status,
 *  fornecedor, valor e a janela da jornada. */
function serviceDetail(voucher: Voucher): TripServiceDetail | null {
  const details = voucher.details;
  switch (details?.kind) {
    case 'FLIGHT':
      return { kind: 'FLIGHT', legs: flightLegs(details.segments) };
    case 'BUS':
      return { kind: 'BUS', legs: busLegs(details.segments) };
    case 'HOTEL':
      return details;
    case 'MOBILITY':
      return mobilityDetail(voucher, details);
    case 'RENTAL':
      return rentalDetail(voucher, details);
    // Despesa e pagamento não têm bloco de detalhe; sem `details`, a fonte ainda não enriqueceu.
    case 'EXPENSE':
    case 'EXPENSE_KM':
    case 'PAYMENT':
    case undefined:
    default:
      return null;
  }
}

/** UM voucher → serviço, fora do contexto da viagem (a tela de aprovações abre
 *  o documento de um voucher que não mora em nenhuma `TripView` do aprovador).
 *  `null` quando o modal não é reconhecido — mesma regra da lista. */
export function voucherService(v: TripView['vouchers'][number], position = 0): TripService | null {
  const modal = canonicalModal(v.modal);
  if (!modal) return null;
  const detail = serviceDetail(v);
  const { at, time } = serviceWindow(v, detail);
  return {
    // O `id` do voucher quando existe; senão modal+posição — a chave nunca pode
    // ser vazia (é a key da lista na linha do tempo).
    key: v.id || `${modal}-${String(position)}`,
    voucher: v,
    modal,
    status: v.status,
    voucherCode: v.voucherCode,
    reservationNumber: v.reservationNumber ?? null,
    supplier: v.supplier,
    amount: v.amount,
    currency: v.currency,
    at,
    time,
    endsAt: serviceEnd(v, detail),
    detail,
  };
}

/** Serviços da viagem p/ o card lateral: um por voucher com modal reconhecido,
 *  na régua do itinerário (`byItinerary`; sem data vai ao fim). O voo remarcado
 *  (`rebookings`) sai da lista e o novo carrega de onde veio; o voo marcado SEM par
 *  fica, e a pill dele diz "Alteração em andamento". */
export function tripServices(trip: TripView): TripService[] {
  const { replaced, fromOf } = rebookingIndex(trip);
  const services: TripService[] = [];
  for (const v of trip.vouchers) {
    if (replaced.has(v.id)) continue;
    const service = voucherService(v, services.length);
    const from = fromOf.get(v.id);
    if (service) services.push(from ? { ...service, rebookedFrom: from } : service);
  }
  return services.sort(byItinerary);
}

// ── Passos do itinerário (painel da viagem) ──────────────────────────────────
// Uma terceira granularidade, e ela existe por causa do AGRUPAMENTO POR DIA do
// painel: um voo ida-e-volta é UM voucher (uma entrada em `tripServices`) mas
// ocupa dois dias do calendário, e tem de aparecer nos dois. O passo não relê os
// vouchers — é uma vista de `tripServices`, carregando o serviço de origem
// (código, status, valor) e, quando o modal tem trechos, a perna.

/** O código de TERCEIRO que a pessoa leva ao balcão: o localizador da companhia
 *  quando a perna tem um, senão o número da reserva do serviço. `kind` viaja com
 *  o código porque a tela os rotula diferente — um hotel não tem localizador,
 *  tem reserva, e é assim que o card do voucher já os chama. */
interface StopReference {
  kind: 'locator' | 'reservation';
  code: string;
}

/** Localizador da PERNA antes da reserva do SERVIÇO: numa ida-e-volta cada
 *  trecho pode ter o seu próprio localizador de companhia, mais específico do
 *  que o número de reserva único do voucher. */
function stopReference(service: TripService, leg: ServiceLeg | null): StopReference | null {
  if (leg?.locator) return { kind: 'locator', code: leg.locator };
  const reservation = service.reservationNumber;
  return reservation ? { kind: 'reservation', code: reservation } : null;
}

/** Um passo do itinerário: um trecho datado (voo/ônibus) ou o serviço inteiro
 *  (hotel, locação, corrida). */
export interface TripStop {
  key: string;
  /** O serviço (voucher) a que o passo pertence — o alvo do "ver voucher". No passo de
   *  vários passageiros, o bilhete MENOS avançado deles: o status que o card mostra. */
  service: TripService;
  /** O bilhete de cada passageiro do passo, na ordem da viagem: só `service` na viagem de
   *  quem voa sozinho; 2+ quando o MESMO voo foi pedido para várias pessoas (#4103) — um
   *  passo só, e não o voo repetido sem nome (`sameJourneys`). */
  services: TripService[];
  /** A perna, quando o modal tem pernas; null nos outros modais. */
  leg: ServiceLeg | null;
  at: Date | null;
  time: string;
  reference: StopReference | null;
}

function serviceLegs(service: TripService): ServiceLeg[] {
  return service.detail?.kind === 'FLIGHT' || service.detail?.kind === 'BUS'
    ? service.detail.legs
    : [];
}

/** Achata `tripServices` em passos: um voo ida-e-volta vira DOIS passos (um por
 *  perna) para poder cair em dois dias do calendário; modais sem perna viram um
 *  passo só, igual ao serviço. Os bilhetes de passageiros diferentes no MESMO voo
 *  são uma jornada só (`sameJourneys`) e dividem o passo. */
export function tripStops(trip: TripView): TripStop[] {
  const stops: TripStop[] = [];
  for (const { lead: service, party } of sameJourneys(tripServices(trip))) {
    const legs = serviceLegs(service);
    if (legs.length === 0) {
      stops.push({
        key: service.key,
        service,
        services: party,
        leg: null,
        at: service.at,
        time: service.time,
        reference: stopReference(service, null),
      });
      continue;
    }
    legs.forEach((lead, index) => {
      const mates = party.map((mate) => serviceLegs(mate)[index]);
      const leg = partyLeg(lead, mates);
      stops.push({
        key: `${service.key}-${leg.key}`,
        service,
        services: party,
        leg,
        at: leg.departAt,
        time: leg.departTime,
        // O código de balcão do grupo é o que vale para todos (o PNR comum); o de um
        // passageiro só não é do card.
        reference: sharedValue(party.map((mate, i) => stopReference(mate, mates[i] ?? null))),
      });
    });
  }
  return stops.sort((a, b) =>
    byItinerary({ at: a.at, modal: a.service.modal }, { at: b.at, modal: b.service.modal }),
  );
}

/** Um dia do itinerário. `date` é null no grupo final dos passos que a fonte não
 *  datou (voucher sem enriquecimento) — eles existem e não podem desaparecer. */
export interface TripStopDay {
  key: string;
  date: Date | null;
  stops: TripStop[];
}

/** Agrupa os passos por dia do calendário local, preservando a ordem cronológica. */
export function groupStopsByDay(stops: TripStop[]): TripStopDay[] {
  const days: TripStopDay[] = [];
  for (const stop of stops) {
    const key = stop.at ? startOfDay(stop.at).toISOString() : 'no-date';
    const last = days.at(-1);
    if (last?.key === key) {
      last.stops.push(stop);
      continue;
    }
    days.push({ key, date: stop.at ? startOfDay(stop.at) : null, stops: [stop] });
  }
  return days;
}

/** Custo da viagem: uma linha por modal (na ordem canônica) + o total. */
export interface TripCost {
  currency: string;
  lines: { modal: ModalKind; amount: number }[];
  total: number;
}

/**
 * O custo da viagem somado por modal. Null quando não há valor algum OU quando os
 * serviços estão em MOEDAS DIFERENTES — somar BRL com USD produziria um total
 * falso, e o painel prefere não mostrar total a mostrar um número errado (os
 * valores por serviço seguem visíveis no voucher).
 */
export function tripCost(services: TripService[]): TripCost | null {
  const priced = services.filter(
    (s): s is TripService & { amount: number; currency: string } =>
      s.amount != null && s.currency != null,
  );
  const currency = priced[0]?.currency;
  if (!currency || priced.some((s) => s.currency !== currency)) return null;
  const byModal = new Map<ModalKind, number>();
  for (const s of priced) byModal.set(s.modal, (byModal.get(s.modal) ?? 0) + s.amount);
  const lines = MODAL_ORDER.flatMap((modal) => {
    const amount = byModal.get(modal);
    return amount == null ? [] : [{ modal, amount }];
  });
  return { currency, lines, total: lines.reduce((sum, l) => sum + l.amount, 0) };
}
